import type { ContractResources } from '../en/contract';

// Typed against the English shape: German must carry exactly the same keys.
export const contract: ContractResources = {
  list: {
    title: 'Verträge',
    searchPlaceholder: 'Suche nach Vertrag, Nummer, Partner oder Fahrzeug...',
    loadError: 'Verträge konnten nicht geladen werden.',
    result: {
      singular: 'Vertrag',
      plural: 'Verträge',
    },
    tabs: {
      purchase: 'Kauf',
      leasing: 'Leasing',
      insurance: 'Versicherung',
    },
    columns: {
      contractKey: 'Vertragsschlüssel',
      name: 'Vertragsname',
      vehicles: 'Fahrzeuge',
      contractPartner: 'Vertragspartner',
      startDate: 'Startdatum',
      endDate: 'Enddatum',
      status: 'Status',
      paymentMethod: 'Zahlungsart',
      externalContractNumber: 'Externe Vertragsnummer',
    },
    columnGroups: {
      base: 'Basisfelder',
    },
    status: {
      active: 'Aktiv',
      ended: 'Beendet',
    },
    vehicles: {
      filterAssigned: 'Fahrzeug zugeordnet',
      filterNotAssigned: 'Kein Fahrzeug zugeordnet',
      hidden: 'Fahrzeug ausgeblendet',
    },
    nextEnd: {
      label: 'Nächstes Ende',
      within90: 'Endet in 90 Tagen',
      within180: 'Endet in 180 Tagen',
      noEnd: 'Kein Ende bekannt',
    },
    empty: {
      purchase: 'Noch keine Kaufverträge.',
      leasing: 'Noch keine Leasingverträge.',
      insurance: 'Noch keine Versicherungsverträge.',
    },
    noResults: {
      title: 'Keine Verträge gefunden',
      description: 'Versuchen Sie einen anderen Suchbegriff oder setzen Sie Suche und Filter zurück.',
      reset: 'Suche und Filter zurücksetzen',
    },
    addContract: 'Vertrag hinzufügen',
  },
  create: {
    title: 'Vertrag hinzufügen',
    noPermission: 'Sie haben keine Berechtigung, in diesem Mandanten Verträge zu erstellen.',
    wizard: {
      step: {
        'type-and-vehicle': { title: 'Vertragsart und Fahrzeug' },
        'master-data': { title: 'Stammdaten' },
        conditions: { title: 'Konditionen' },
        commercial: { title: 'Kaufmännisches' },
      },
      conditionsPlaceholder: 'Konditionen für diese Vertragsart sind noch nicht verfügbar.',
      noVehicleYet: 'Noch kein Fahrzeug',
      nav: {
        stepCount: 'Schritt {{current}} von {{total}}',
        fieldCount: '{{filled}}/{{total}} ausgefüllt',
        back: 'Zurück',
        next: 'Weiter',
        cancel: 'Abbrechen',
        submit: 'Vertrag anlegen',
        submitting: 'Legt an…',
      },
      typeSwitchConfirm: {
        title: 'Vertragsart wechseln?',
        description:
          'Die Eingaben der bisherigen Fachfelder werden verworfen. Stammdaten und kaufmännische Angaben bleiben erhalten.',
        confirm: 'Wechseln und verwerfen',
        cancel: 'Abbrechen',
      },
      validation: {
        blockedNext: 'Bitte füllen Sie {{field}} aus, um fortzufahren.',
      },
    },
    fields: {
      name: 'Vertragsbezeichnung',
      type: 'Vertragsart',
      contractPartner: 'Vertragspartner',
      contactPerson: 'Ansprechpartner',
      startDate: 'Vertragsbeginn',
      purchaseDate: 'Kaufdatum',
      currency: 'Währung',
      externalContractNumber: 'Externe Vertragsnummer',
      vehicleCondition: 'Fahrzeugzustand',
      mileageAtPurchase: 'Kilometerstand bei Kauf',
      ordinaryTermination: 'Ordentliche Kündigung möglich',
      noticePeriod: 'Kündigungsfrist',
      automaticRenewal: 'Automatische Verlängerung',
    },
    units: {
      months: 'Monate',
    },
    vehicleCondition: {
      new: 'Neu',
      used: 'Gebraucht',
    },
    yesNo: {
      yes: 'Ja',
      no: 'Nein',
    },
    // Placeholders are names, not interface copy (contracts_v0.1.md, Contract
    // position — "Placeholders") — identical in every language, not translated.
    placeholders: {
      name: {
        leasing: 'Leasing Audi A6 Avant',
        purchase: 'Kauf Audi A6 Avant',
        insurance: 'Kfz Versicherung Audi A6',
      },
      contractPartner: {
        leasing: 'ALD Automotive',
        purchase: 'Autohaus Sonnenberg GmbH',
        insurance: 'HUK-COBURG Versicherung',
      },
      externalContractNumber: {
        leasing: 'ALD-884213',
        purchase: 'AH-2025-0912',
        insurance: 'VS-2026-4711',
      },
    },
    validation: {
      required: 'Dieses Feld ist erforderlich.',
    },
    actions: {
      save: 'Vertrag anlegen',
      saving: 'Speichert…',
      cancel: 'Abbrechen',
    },
    toast: {
      created: 'Vertrag angelegt',
      saveFailed: 'Vertrag konnte nicht angelegt werden.',
    },
  },
  detail: {
    actions: {
      edit: 'Bearbeiten',
      save: 'Speichern',
      saving: 'Speichert…',
      cancel: 'Abbrechen',
    },
    tabs: {
      contractData: 'Vertragsdaten',
      commercial: 'Kaufmännisches',
    },
    toast: {
      saved: 'Vertrag gespeichert',
      saveFailed: 'Vertrag konnte nicht gespeichert werden.',
      concurrentEdit:
        'Der Vertrag wurde zwischenzeitlich geändert. Ihre Eingaben bleiben erhalten — bitte prüfen und erneut speichern.',
      clearNotSupported: 'Dieses Feld kann noch nicht geleert werden — der vorherige Wert wurde beibehalten.',
    },
    noAccountingAccess: 'Ihre Rolle darf keine kaufmännischen Details sehen.',
    currencyLocked: 'Nicht mehr änderbar — der Vertrag wurde bereits angelegt.',
  },
  commercial: {
    fields: {
      billingBasis: 'Abrechnungsgrundlage',
      firstDueDate: 'Erste Fälligkeit',
      dueDay: 'Fälligkeitstag',
      paymentPeriod: 'Zahlungsperiode',
      paymentTiming: 'Zahlungszeitpunkt',
      paymentMethod: 'Zahlungsart',
    },
    billingBasis: {
      contract: 'Vertrag',
      permanentInvoice: 'Dauerrechnung',
      individualInvoice: 'Einzelrechnung',
    },
    paymentPeriod: {
      monthly: 'Monatlich',
      quarterly: 'Quartalsweise',
      semiAnnually: 'Halbjährlich',
      annually: 'Jährlich',
      oneOff: 'Einmalig',
    },
    paymentTiming: {
      advance: 'Vorschüssig',
      arrears: 'Nachschüssig',
      unknown: 'Unbekannt',
    },
    paymentMethod: {
      bankTransfer: 'Überweisung',
      directDebit: 'Lastschrift',
      financing: 'Finanzierung',
      other: 'Sonstige',
    },
  },
  vehicles: {
    title: 'Fahrzeuge',
    assign: 'Fahrzeug zuordnen',
    assignDisabledReason: 'Ein Vertrag trägt genau ein Fahrzeug. Entfernen Sie das laufende zuerst.',
    remove: 'Entfernen',
    ended: 'Beendet',
    noVehicleAssigned: 'Noch kein Fahrzeug zugeordnet',
    vehicleHidden: 'Fahrzeug ausgeblendet',
    loading: 'Fahrzeuge werden geladen…',
    loadError: 'Fahrzeuge konnten nicht geladen werden.',
    columns: {
      licensePlate: 'Kennzeichen',
      vin: 'FIN',
      validFrom: 'Gültig ab',
      validUntil: 'Gültig bis',
    },
    assignDialog: {
      title: 'Fahrzeug zuordnen',
      vehicleLabel: 'Fahrzeug',
      vehiclePlaceholder: 'Suche nach Kennzeichen oder FIN…',
      validFrom: 'Gültig ab',
      validUntil: 'Gültig bis',
      submit: 'Zuordnen',
      submitting: 'Ordnet zu…',
      cancel: 'Abbrechen',
    },
    removeConfirm: {
      title: '{{vehicle}} aus diesem Vertrag entfernen?',
      description: 'Das Fahrzeug bleibt in der Vertragshistorie sichtbar.',
      confirm: 'Entfernen',
      confirming: 'Entfernt…',
      cancel: 'Abbrechen',
    },
    toast: {
      assigned: '1 Fahrzeug zugeordnet',
      removed: 'Fahrzeug entfernt',
      assignFailed: 'Fahrzeug konnte nicht zugeordnet werden.',
      removeFailed: 'Fahrzeug konnte nicht entfernt werden.',
    },
  },
  status: {
    values: {
      active: 'Aktiv',
      ended: 'Beendet',
    },
    endingDialog: {
      title: 'Vertrag beenden?',
      type: 'Art der Beendigung',
      typeValues: {
        regularExpiry: 'Regulär ausgelaufen',
        termination: 'Gekündigt',
      },
      effectiveDate: 'Beendet am',
      noticeGivenDate: 'Gekündigt am',
      wirkungZum: 'Wirkung zum',
      agreedEndDate: 'Vereinbart war',
      noNoticePeriod: 'Keine Kündigungsfrist erfasst — bitte den Tag eintragen, zu dem die Kündigung wirkt.',
      reason: 'Grund',
      reasonPlaceholder: 'Welcher Grund?',
      reasonText: 'Grund (sonstige)',
      confirm: 'Beenden',
      cancel: 'Abbrechen',
      reasons: {
        termExpired: 'Laufzeit abgelaufen',
        totalMileageReached: 'Gesamtlaufleistung erreicht',
        notRenewed: 'Nicht verlängert',
        terminatedByUs: 'Kündigung durch uns',
        terminatedByPartner: 'Kündigung durch den Vertragspartner',
        terminationAgreement: 'Aufhebungsvereinbarung',
        vehicleSold: 'Fahrzeug verkauft',
        totalLoss: 'Totalschaden',
        vehicleReturnedEarly: 'Fahrzeug vorzeitig zurückgegeben',
        paymentDefault: 'Zahlungsausfall',
        other: 'Sonstiger Grund',
      },
    },
    endConfirm: {
      title: 'Vertrag beenden?',
      description_one:
        '{{count}} Position hat kein Gültig-bis. Sie bleibt unverändert — der Vertrag gilt trotzdem als beendet.',
      description_other:
        '{{count}} Positionen haben kein Gültig-bis. Sie bleiben unverändert — der Vertrag gilt trotzdem als beendet.',
      confirm: 'Beenden',
      cancel: 'Abbrechen',
    },
    reactivateConfirm: {
      title: 'Vertrag wieder aktiv setzen?',
      description: 'Der Status wechselt zu aktiv. Das erfasste Ende samt Grund wird dabei entfernt.',
      confirm: 'Aktiv setzen',
      confirming: 'Wird aktiv gesetzt…',
      cancel: 'Abbrechen',
    },
    endingCard: {
      title: 'Beendigung',
      type: 'Art der Beendigung',
      noticeGivenDate: 'Gekündigt am',
      effectiveDate: 'Wirkung zum',
      agreedEndDate: 'Vereinbart war',
      reason: 'Grund',
      recorded: 'Erfasst',
      recordedBy: '{{name}} am {{date}}',
      noReasonRecorded: 'Nicht erfasst',
    },
    toast: {
      ended: 'Vertrag beendet',
      terminated: 'Vertrag gekündigt',
      active: 'Vertrag wieder aktiv',
      endFailed: 'Vertrag konnte nicht beendet werden.',
      reactivateFailed: 'Vertrag konnte nicht reaktiviert werden.',
    },
  },
};
